// src/pages/GroupManagerPage.tsx — Sign-In System (Build 2) Group Manager.
// Route /group-manager. Admin-only app for creating chat groups + managing
// membership. Prototype State 13. Distinct from departments and Issue Manager
// permission groups. Heather #7A5D8C lane.
//
// Endpoints (all SSO-admin, enforced server-side under /api/access/*):
//   GET    /api/access/groups
//   POST   /api/access/groups
//   PATCH  /api/access/groups/:id
//   DELETE /api/access/groups/:id            (soft archive)
//   GET    /api/access/groups/:id/members
//   PUT    /api/access/groups/:id/members    { userIds:[...] }
// The add-member picker reuses GET /api/access/users (existing admin list).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import { TopBar } from "../components/ui";
import { G, GF, initialsFromName } from "../components/groupqm/groupTokens";

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

interface AdminGroup {
  id: number;
  code: string;
  displayName: string;
  description: string | null;
  isSelfJoinable: boolean;
  autoCloseIdleMinutes: number | null;
  memberCount: number;
  signedInCount: number;
  createdAt: string;
}
interface Member {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  joinedVia: string;
  joinedAt: string;
  signedIn: boolean;
}
interface AccessUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function GroupManagerPage() {
  const navigate = useNavigate();
  const { me } = useCurrentUser();

  const [groups, setGroups] = useState<AdminGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const loadGroups = useCallback(async () => {
    try {
      const r = await apiFetch<{ groups: AdminGroup[] }>("/api/access/groups");
      setGroups(r.groups);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't load groups.");
    }
  }, []);

  useEffect(() => { void loadGroups(); }, [loadGroups]);

  useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => setFlash(null), 2600);
    return () => window.clearTimeout(t);
  }, [flash]);

  const filtered = useMemo(() => {
    if (!groups) return [];
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.displayName.toLowerCase().includes(q) || g.code.toLowerCase().includes(q));
  }, [groups, search]);

  // Admin gate — PIN admins / non-admins never see this app, but guard the
  // direct-navigation case too (the API would 403 anyway).
  if (me && !me.isSsoAdmin) {
    return (
      <div>
        <TopBar title="Group Manager" sub="Chat groups" onBack={() => navigate("/apps")} />
        <div style={{ padding: 24, fontFamily: GF.serif, fontStyle: "italic", color: G.inkSoft }}>
          This app is for NYMR Entra admins only.
        </div>
      </div>
    );
  }

  return (
    <div>
      <TopBar title="Group Manager" sub="Chat groups" onBack={() => navigate("/apps")} />
      <div style={{ padding: "16px 16px 28px", display: "flex", flexDirection: "column", gap: 12 }}>
        {error && (
          <div role="alert" style={{ padding: 12, borderRadius: 12, background: "#F8D9D2", border: "1px solid rgba(184,58,43,0.22)", fontFamily: GF.body, fontSize: 13, color: "#8A2A1F", fontWeight: 700 }}>
            {error}
          </div>
        )}
        {flash && (
          <div role="status" style={{ padding: 12, borderRadius: 12, background: G.grpSurf, border: `1px solid ${G.grpLt}`, color: G.grpDk, fontFamily: GF.body, fontWeight: 700, fontSize: 13 }}>
            {flash}
          </div>
        )}

        {/* Toolbar */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search groups…"
            aria-label="Search groups"
            style={{
              flex: 1, minHeight: 42, padding: "10px 12px", background: G.card, border: `1px solid ${G.hairline}`,
              borderRadius: 12, fontFamily: GF.body, fontSize: 14, color: G.ink, outline: "none", boxSizing: "border-box",
            }}
          />
          <button
            type="button"
            onClick={() => setCreating((v) => !v)}
            style={{
              minHeight: 42, padding: "10px 14px", background: G.grp, color: "#fff", border: "none", borderRadius: 12,
              fontFamily: GF.body, fontSize: 13.5, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 6px rgba(122,93,140,0.22)",
            }}
          >
            + New group
          </button>
        </div>

        {creating && (
          <CreateGroupForm
            onCancel={() => setCreating(false)}
            onCreated={() => { setCreating(false); setFlash("Group created."); void loadGroups(); }}
            onError={setError}
          />
        )}

        {/* Group list */}
        {!groups && <div style={{ height: 160, borderRadius: 16, background: G.pageWarm, border: `1px solid ${G.hairlineLt}` }} aria-hidden="true" />}
        {groups && filtered.length === 0 && (
          <div style={{ padding: "24px 12px", textAlign: "center", fontFamily: GF.serif, fontStyle: "italic", color: G.inkMute }}>
            {groups.length === 0 ? "No groups yet. Create the first one above." : "No groups match your search."}
          </div>
        )}
        {filtered.map((g) => (
          <div key={g.id}>
            <GroupRow
              group={g}
              selected={selectedId === g.id}
              onToggle={() => setSelectedId((cur) => (cur === g.id ? null : g.id))}
            />
            {selectedId === g.id && (
              <GroupDetail
                group={g}
                onChanged={() => { setFlash("Saved."); void loadGroups(); }}
                onArchived={() => { setSelectedId(null); setFlash("Group archived."); void loadGroups(); }}
                onError={setError}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group list row
// ---------------------------------------------------------------------------

function GroupRow({ group, selected, onToggle }: { group: AdminGroup; selected: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={selected}
      style={{
        width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
        background: G.card, border: `1px solid ${selected ? G.grpDk : G.grpLt}`,
        borderRadius: selected ? "16px 16px 0 0" : 16, cursor: "pointer",
      }}
    >
      <span style={{
        width: 40, height: 40, borderRadius: 12, background: `linear-gradient(135deg, ${G.grpLt}, ${G.grp})`,
        color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: GF.serif,
        fontWeight: 700, fontSize: 13, flexShrink: 0,
      }}>
        {initialsFromName(group.displayName, group.code)}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontFamily: GF.serif, fontSize: 16, fontWeight: 700, color: G.ink, letterSpacing: "-0.2px" }}>
          {group.displayName}
        </span>
        <span style={{ display: "block", fontFamily: GF.mono, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase", color: G.inkMute, marginTop: 2 }}>
          {group.memberCount} members · {group.signedInCount} on · {group.isSelfJoinable ? "self-join" : "admin-add"}
        </span>
      </span>
      <span style={{ color: G.grpDk, fontWeight: 800, fontSize: 14 }}>{selected ? "⌃" : "›"}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Group detail — settings + membership
// ---------------------------------------------------------------------------

function GroupDetail({
  group, onChanged, onArchived, onError,
}: {
  group: AdminGroup;
  onChanged: () => void;
  onArchived: () => void;
  onError: (msg: string) => void;
}) {
  const [displayName, setDisplayName] = useState(group.displayName);
  const [description, setDescription] = useState(group.description ?? "");
  const [selfJoin, setSelfJoin] = useState(group.isSelfJoinable);
  const [idle, setIdle] = useState<string>(group.autoCloseIdleMinutes === null ? "" : String(group.autoCloseIdleMinutes));
  const [savingSettings, setSavingSettings] = useState(false);

  const [members, setMembers] = useState<Member[] | null>(null);
  const [picker, setPicker] = useState(false);

  const loadMembers = useCallback(async () => {
    try {
      const r = await apiFetch<{ members: Member[] }>(`/api/access/groups/${group.id}/members`);
      setMembers(r.members);
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Couldn't load members.");
    }
  }, [group.id, onError]);

  useEffect(() => { void loadMembers(); }, [loadMembers]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const idleNum = idle.trim() === "" ? null : Number(idle.trim());
      if (idleNum !== null && (!Number.isInteger(idleNum) || idleNum < 5 || idleNum > 1440)) {
        onError("Auto-close idle minutes must be a whole number 5–1440, or blank to inherit.");
        setSavingSettings(false);
        return;
      }
      await apiFetch(`/api/access/groups/${group.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          displayName: displayName.trim(),
          description: description.trim() === "" ? null : description.trim(),
          isSelfJoinable: selfJoin,
          autoCloseIdleMinutes: idleNum,
        }),
      });
      onChanged();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Couldn't save settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const putMembers = async (userIds: number[]) => {
    try {
      await apiFetch(`/api/access/groups/${group.id}/members`, {
        method: "PUT",
        body: JSON.stringify({ userIds }),
      });
      await loadMembers();
      onChanged();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Couldn't update members.");
    }
  };

  const removeMember = (userId: number) => {
    if (!members) return;
    void putMembers(members.filter((m) => m.userId !== userId).map((m) => m.userId));
  };

  const addMembers = (toAdd: number[]) => {
    if (!members) return;
    const next = Array.from(new Set([...members.map((m) => m.userId), ...toAdd]));
    setPicker(false);
    void putMembers(next);
  };

  return (
    <div style={{ border: `1px solid ${G.grpDk}`, borderTop: "none", borderRadius: "0 0 16px 16px", background: G.grpSurf, padding: "14px 14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Members */}
      <div>
        <Label>Members</Label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
          {members === null && <span style={{ fontFamily: GF.body, fontSize: 12, color: G.inkMute }}>Loading…</span>}
          {members && members.length === 0 && <span style={{ fontFamily: GF.serif, fontStyle: "italic", fontSize: 12.5, color: G.inkMute }}>No members yet.</span>}
          {members?.map((m) => (
            <span key={m.userId} style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 6px 5px 10px",
              background: G.card, border: `1px solid ${m.signedIn ? G.grp : G.hairline}`, borderRadius: 999,
              fontFamily: GF.body, fontSize: 12.5, fontWeight: 700, color: G.ink,
            }}>
              {m.signedIn && <span aria-hidden="true" title="Signed in for duty" style={{ width: 7, height: 7, borderRadius: "50%", background: G.grp }} />}
              {m.firstName} {m.lastName}
              <button
                type="button"
                aria-label={`Remove ${m.firstName} ${m.lastName}`}
                onClick={() => removeMember(m.userId)}
                style={{ width: 18, height: 18, borderRadius: 9, border: "none", background: G.grpBg, color: G.grpDk, cursor: "pointer", fontWeight: 800, lineHeight: 1, fontSize: 12 }}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => setPicker(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 12px", background: G.grp, color: "#fff", border: "none", borderRadius: 999, fontFamily: GF.body, fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}
          >
            + Add member
          </button>
        </div>
      </div>

      {/* Settings */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Label>Settings</Label>
        <TextRow label="Display name" value={displayName} onChange={setDisplayName} />
        <TextRow label="Description" value={description} onChange={setDescription} placeholder="Optional" />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ToggleChip on={selfJoin} onClick={() => setSelfJoin((v) => !v)} label={selfJoin ? "Self-joinable" : "Admin-add only"} />
          <div style={{ flex: 1 }}>
            <TextRow label="Auto-close idle (min)" value={idle} onChange={setIdle} placeholder="inherit (90)" />
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
          <span style={{ fontFamily: GF.mono, fontSize: 10, color: G.inkMute }}>code: {group.code} (locked)</span>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() => { if (window.confirm(`Archive "${group.displayName}"? It will be hidden from compose and self-join, but history is preserved.`)) void archive(group.id, onArchived, onError); }}
            style={{ padding: "8px 12px", background: "transparent", color: "#8A2A1F", border: "1px solid rgba(184,58,43,0.4)", borderRadius: 10, fontFamily: GF.body, fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}
          >
            Archive
          </button>
          <button
            type="button"
            onClick={saveSettings}
            disabled={savingSettings}
            style={{ padding: "8px 16px", background: G.grp, color: "#fff", border: "none", borderRadius: 10, fontFamily: GF.body, fontSize: 13, fontWeight: 800, cursor: "pointer", opacity: savingSettings ? 0.7 : 1 }}
          >
            {savingSettings ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>

      {picker && members && (
        <MemberPicker
          existingIds={new Set(members.map((m) => m.userId))}
          onCancel={() => setPicker(false)}
          onAdd={addMembers}
          onError={onError}
        />
      )}
    </div>
  );
}

async function archive(id: number, onArchived: () => void, onError: (m: string) => void) {
  try {
    await apiFetch(`/api/access/groups/${id}`, { method: "DELETE" });
    onArchived();
  } catch (e) {
    onError(e instanceof ApiError ? e.message : "Couldn't archive group.");
  }
}

// ---------------------------------------------------------------------------
// Create group form
// ---------------------------------------------------------------------------

function CreateGroupForm({ onCancel, onCreated, onError }: { onCancel: () => void; onCreated: () => void; onError: (m: string) => void }) {
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [selfJoin, setSelfJoin] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const c = code.trim().toLowerCase();
    if (!/^[a-z][a-z0-9_]{1,46}$/.test(c)) {
      onError("Code must be lowercase letters/digits/underscores, 2–47 chars, starting with a letter.");
      return;
    }
    if (displayName.trim().length === 0) { onError("Display name is required."); return; }
    setBusy(true);
    try {
      await apiFetch("/api/access/groups", {
        method: "POST",
        body: JSON.stringify({
          code: c,
          displayName: displayName.trim(),
          description: description.trim() === "" ? null : description.trim(),
          isSelfJoinable: selfJoin,
        }),
      });
      onCreated();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Couldn't create group.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ border: `1px solid ${G.grpLt}`, background: G.card, borderRadius: 16, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontFamily: GF.serif, fontStyle: "italic", fontSize: 14, fontWeight: 700, color: G.grpDk }}>New group</div>
      <TextRow label="Code (permanent)" value={code} onChange={setCode} placeholder="e.g. ops_north" />
      <TextRow label="Display name" value={displayName} onChange={setDisplayName} placeholder="e.g. Ops — North" />
      <TextRow label="Description" value={description} onChange={setDescription} placeholder="Optional" />
      <ToggleChip on={selfJoin} onClick={() => setSelfJoin((v) => !v)} label={selfJoin ? "Self-joinable" : "Admin-add only"} />
      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
        <button type="button" onClick={onCancel} style={{ flex: 1, minHeight: 42, background: "transparent", color: G.inkSoft, border: `1px solid ${G.hairline}`, borderRadius: 12, fontFamily: GF.body, fontSize: 14, fontWeight: 800, cursor: "pointer" }}>Cancel</button>
        <button type="button" onClick={submit} disabled={busy} style={{ flex: 1, minHeight: 42, background: G.grp, color: "#fff", border: "none", borderRadius: 12, fontFamily: GF.body, fontSize: 14, fontWeight: 800, cursor: "pointer", opacity: busy ? 0.7 : 1 }}>{busy ? "Creating…" : "Create group"}</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Member picker — reuses GET /api/access/users
// ---------------------------------------------------------------------------

function MemberPicker({ existingIds, onCancel, onAdd, onError }: { existingIds: Set<number>; onCancel: () => void; onAdd: (ids: number[]) => void; onError: (m: string) => void }) {
  const [users, setUsers] = useState<AccessUser[] | null>(null);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ users: AccessUser[] }>("/api/access/users")
      .then((r) => { if (!cancelled) setUsers(r.users.filter((u) => !existingIds.has(u.id))); })
      .catch((e) => { if (!cancelled) onError(e instanceof ApiError ? e.message : "Couldn't load users."); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(q));
  }, [users, search]);

  const toggle = (id: number) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div role="dialog" aria-modal="true" aria-label="Add members" onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(43,32,20,0.34)", backdropFilter: "blur(2px)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 220 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 520, maxHeight: "80vh", background: G.page, borderRadius: "22px 22px 0 0", padding: "16px 18px 20px", boxShadow: "0 -16px 40px rgba(122,93,140,0.18)", display: "flex", flexDirection: "column" }}>
        <div style={{ width: 38, height: 4, borderRadius: 2, background: G.hairline, margin: "-6px auto 12px" }} />
        <div style={{ fontFamily: GF.serif, fontStyle: "italic", fontSize: 15, fontWeight: 700, color: G.grpDk, marginBottom: 10 }}>Add members</div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search people…"
          aria-label="Search people"
          style={{ minHeight: 42, padding: "10px 12px", background: G.card, border: `1px solid ${G.hairline}`, borderRadius: 12, fontFamily: GF.body, fontSize: 14, color: G.ink, outline: "none", marginBottom: 10, boxSizing: "border-box" }}
        />
        <div style={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
          {users === null && <span style={{ fontFamily: GF.body, fontSize: 13, color: G.inkMute }}>Loading people…</span>}
          {users && filtered.length === 0 && <span style={{ fontFamily: GF.serif, fontStyle: "italic", fontSize: 13, color: G.inkMute }}>No matching people.</span>}
          {filtered.map((u) => {
            const on = picked.has(u.id);
            return (
              <button key={u.id} type="button" onClick={() => toggle(u.id)} aria-pressed={on} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", textAlign: "left",
                background: on ? G.grpSurf : G.card, border: `1.5px solid ${on ? G.grpDk : G.hairline}`, borderRadius: 12, cursor: "pointer",
              }}>
                <span style={{ width: 22, height: 22, borderRadius: 6, border: `1.5px solid ${on ? G.grpDk : G.hairline}`, background: on ? G.grp : "transparent", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{on ? "✓" : ""}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontFamily: GF.body, fontSize: 14, fontWeight: 700, color: G.ink }}>{u.first_name} {u.last_name}</span>
                  <span style={{ display: "block", fontFamily: GF.mono, fontSize: 10.5, color: G.inkMute }}>{u.email}</span>
                </span>
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button type="button" onClick={onCancel} style={{ flex: 1, minHeight: 44, background: "transparent", color: G.inkSoft, border: `1px solid ${G.hairline}`, borderRadius: 12, fontFamily: GF.body, fontSize: 14, fontWeight: 800, cursor: "pointer" }}>Cancel</button>
          <button type="button" onClick={() => onAdd([...picked])} disabled={picked.size === 0} style={{ flex: 1, minHeight: 44, background: G.grp, color: "#fff", border: "none", borderRadius: 12, fontFamily: GF.body, fontSize: 14, fontWeight: 800, cursor: "pointer", opacity: picked.size === 0 ? 0.45 : 1 }}>
            Add {picked.size > 0 ? `(${picked.size})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small shared inputs
// ---------------------------------------------------------------------------

function Label({ children }: { children: React.ReactNode }) {
  return <div style={{ fontFamily: GF.body, fontSize: 10.5, fontWeight: 800, letterSpacing: "1.2px", textTransform: "uppercase", color: G.inkMute }}>{children}</div>;
}

function TextRow({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ display: "block", fontFamily: GF.body, fontSize: 11, fontWeight: 700, color: G.inkSoft, marginBottom: 4 }}>{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: "100%", minHeight: 40, padding: "9px 12px", background: G.card, border: `1px solid ${G.hairline}`, borderRadius: 10, fontFamily: GF.body, fontSize: 14, color: G.ink, outline: "none", boxSizing: "border-box" }}
      />
    </label>
  );
}

function ToggleChip({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} style={{
      display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 999, cursor: "pointer",
      background: on ? G.grp : G.card, color: on ? "#fff" : G.inkSoft, border: `1.5px solid ${on ? G.grpDk : G.hairline}`,
      fontFamily: GF.body, fontSize: 12.5, fontWeight: 800,
    }}>
      <span style={{ width: 24, height: 14, borderRadius: 999, background: on ? "rgba(255,255,255,0.4)" : G.hairline, position: "relative", flexShrink: 0 }}>
        <span style={{ position: "absolute", top: 1, left: on ? 11 : 1, width: 12, height: 12, borderRadius: "50%", background: "#fff", transition: "left 0.12s" }} />
      </span>
      {label}
    </button>
  );
}
